import { z } from "zod";
import { CapSchema, type AskableCap, type Cap, type PluginCap } from "./capabilities.ts";
import { EventKindSchema } from "./events.ts";
import { ContainerDisciplineSchema } from "./layout.ts";
import { MAX_STREAM_DESCRIPTORS, StreamDescriptorSchema, streamVocabulary } from "./stream.ts";
import { MachineHalfSchema, type TerminalRuntime } from "./jobs.ts";
import { HarnessDefinitionSchema } from "./agents.ts";
import { ManifoldRefSchema, type ManifoldRef } from "./uri.ts";
import type { GrantNode, GrantReach } from "./grants.ts";
import type { TerminalExecution } from "./machine.ts";
import { compileJsonProjection, JsonProjectionError, JsonProjectionSchema } from "./services.ts";
import {
  DEFAULT_ELEMENT_PLACEMENT_TRAITS,
  DisciplineDefSchema,
  PlacementTraitsSchema,
  type DisciplineDeclaration,
} from "./placement.ts";

/**
 * The plugin vocabulary: what a plugin IS on the wire, and what invoking one of its
 * actions can answer. Everything here is inert DATA — a manifest declares, it never
 * executes — so the same shapes describe an in-process core plugin today and an isolated
 * third-party runner later without a second format.
 *
 * A plugin id is dotted and namespaced (`core.terminals`): the leading segment is the
 * authority publishing it, so two authors can both ship a `terminals` plugin without a
 * collision, and a collision inside one namespace is a named refusal rather than
 * shadowing (AXIOMS.md D5).
 */
export const PLUGIN_ID_PATTERN = /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*){1,2}$/;
export const PluginIdSchema = z.string().regex(PLUGIN_ID_PATTERN).max(64);
export type PluginId = z.infer<typeof PluginIdSchema>;

/**
 * The namespace reserved for the ENGINE's own doors. Enablement itself is a door — the
 * thing that turns plugins on cannot be a plugin that can be turned off — so it is
 * published as an ordinary roster row under `engine.plugins`, with the same manifest and
 * the same action schemas as everything else, distinguished only by `source: "builtin"`.
 * One dispatch ladder, one vocabulary, no privileged second door.
 *
 * The prefix is a RESERVATION: assembly refuses a non-builtin plugin claiming an id
 * under it, because a plugin that could publish `engine.*` could impersonate the engine to
 * a client reading the roster.
 */
export const ENGINE_NAMESPACE_PREFIX = "engine.";

/**
 * The namespace manifold's own shipped plugins are authored under, and NOTHING ELSE. It
 * confers no privilege whatsoever: a `core.` row is dispatched, authorized, disabled and
 * purged by exactly the rules a stranger's row is, and the engine has no branch anywhere that
 * reads this prefix to decide what something may do. That is the point of publishing it —
 * "core is not privileged" is checkable when the prefix means AUTHORSHIP and nothing more.
 *
 * Authorship is still worth defending, so the prefix is also a RESERVATION, symmetric with
 * `ENGINE_NAMESPACE_PREFIX`: assembly refuses a manifest under `core.` that the shipped
 * distribution did not register, because a stranger's plugin publishing `core.anything`
 * composes cleanly and reads as official to every principal and agent looking at the roster.
 * The permitted set is DERIVED from the distribution's own registration files and handed to
 * `assembleRoster` (`AssemblyEnv.distribution`), never written out a second time here — a
 * hand-kept list of "our" plugins in this package is exactly the second door docs/CONTRACTS.md §One authoritative implementation
 * forbids, and it would be the thing that goes stale the first time a plugin is added.
 */
export const CORE_NAMESPACE_PREFIX = "core.";

/**
 * A contribution's name INSIDE its plugin. Every published name is the pair — an action is
 * `${manifest.id}.${local}` on the wire — so a plugin can never name anything outside its
 * own namespace and a full name always says who owns it.
 *
 * Exported because the assembly engine validates every action's local name against this
 * exact rule before it builds a full name: one door per concept, so a local name is legal
 * here and there or nowhere.
 *
 * Interior capitals are allowed because the ratified vocabulary uses them where the name is
 * a verb phrase (`engine.plugins.setEnabled`); a name still starts lowercase and carries no
 * dot, so the segment boundary in a full name stays unambiguous.
 */
export const LOCAL_NAME_PATTERN = /^[a-z][a-zA-Z0-9-]*$/;
export const LocalNameSchema = z.string().regex(LOCAL_NAME_PATTERN).max(32);
export type LocalName = z.infer<typeof LocalNameSchema>;

/**
 * A PLUGIN'S OWN CAPABILITY (ADR 0035): `<pluginId>:<name>`, and the pair is the whole law.
 *
 * The engine's vocabulary is a closed enum (`CAPS`) because every name in it is authority over
 * a door the engine owns. A plugin that acts per machine, per repository, per anything the
 * engine has no word for needs a name of its own, and the only safe place to put it is the
 * namespace the plugin already owns: the same `<pluginId>.<local>` discipline every other
 * published name follows, with `:` instead of `.` so a cap can never be mistaken for an
 * action. Declaring `atyrode.babel:archive` claims nothing outside `atyrode.babel`, and the
 * manifest check below refuses a manifest that names another plugin's namespace.
 *
 * It lives HERE rather than in `capabilities.ts` for one reason and it is not taste: a
 * namespace IS a plugin id, so the form is `PluginIdSchema` and `LocalNameSchema` composed,
 * and those laws live in this file. `capabilities.ts` holds the TYPES (`PluginCap`,
 * `AuthoredCap`, `AskableCap`) because the vocabulary is its concept and a type needs no
 * import; putting the SCHEMA there would mean importing this file from the one file it
 * already imports, and a cycle between two modules that both build schemas at import time is
 * a boot-order bug waiting for the first reordered import.
 *
 * The bound is the two ids' own bounds plus the separator; `templateLiteral` composes the
 * parts' patterns but not their `max`, so the length is a check of its own rather than an
 * assumption.
 */
export const MAX_PLUGIN_CAP_LENGTH = 64 + 1 + 32;

/**
 * `PluginCap` is a TEMPLATE type carrying the plugin id's mandatory dot, which is what stops
 * `Cap | PluginCap` from collapsing into "any string with a colon" — and `templateLiteral`
 * cannot infer that, because a part built from `z.string().regex(...)` infers as `string` and
 * the dot lives inside the id's own pattern. So the schema states the type it validates, and
 * the assertion is sound BY THE PATTERN: `PluginIdSchema` admits nothing without at least one
 * `.`, and every accepted value is therefore `${string}.${string}:${string}`.
 */
export const PluginCapSchema = z
  .templateLiteral([PluginIdSchema, ":", LocalNameSchema])
  .check(z.maxLength(MAX_PLUGIN_CAP_LENGTH)) as unknown as z.ZodType<PluginCap, string>;

/** A capability as anything that DECLARES one names it: the engine's own, or a plugin's. */
export const AuthoredCapSchema = z.union([CapSchema, PluginCapSchema]);

/**
 * A capability an authority question may name — the wildcard excluded, because `*` is the
 * engine's "everything" and an authority question is asked about one capability at a node.
 */
export const AskableCapSchema = z.union([CapSchema.exclude(["*"]), PluginCapSchema]);

/** Conjunctive authority added by an installed, sealed action preparer. */
export interface PreparedRequirement {
  readonly cap: AskableCap;
  readonly node: GrantNode;
  readonly reach: GrantReach;
}

export interface PreparedActionInput {
  readonly args: unknown;
  readonly targets: readonly ManifoldRef[];
  readonly additionalRequirements?: readonly PreparedRequirement[];
}

/** Only nonsecret facts; this is never a narrowed or cast mutable action context. */
export interface ActionPreparationCtx {
  readonly terminals: {
    resolveMachine(input: {
      readonly machineId?: string;
      readonly runtime?: TerminalRuntime;
    }): Promise<{
      readonly machineId: string;
      readonly terminalHostId: string | null;
      readonly terminalExecution: TerminalExecution | null;
    }>;
    stored(terminalId: string): Promise<{
      readonly machineId: string;
      readonly containerId: string;
      readonly governed: boolean;
      readonly nativeRequirements?: readonly PreparedRequirement[];
    } | null>;
  };
  readonly containers: {
    placement(containerId: string): Promise<"element" | "tile">;
  };
  readonly native: {
    /** Plans exact native authority; opaque literal inputs are validated only at effect admission. */
    demand(
      runtime: TerminalRuntime,
      machineId: string,
      containerId: string,
    ): Promise<readonly PreparedRequirement[]>;
  };
}

export type ActionPreparer = (
  ctx: ActionPreparationCtx,
  args: never,
) => Promise<PreparedActionInput>;

export interface ActionPreparationDef {
  /** Pinned additional-capability ceiling, not a guest-returned assertion. */
  readonly caps: readonly AskableCap[];
  readonly prepare: ActionPreparer;
}

/**
 * WHOSE capability this is, or null for one of the engine's own (and for anything malformed).
 *
 * The one reader of a capability's namespace, so the split is spelled once: the declaring
 * plugin is everything before the first `:`, and a name the schema refuses has no namespace at
 * all rather than a guessed one.
 */
export function pluginCapNamespace(cap: string): string | null {
  if (!PluginCapSchema.safeParse(cap).success) return null;
  return cap.slice(0, cap.indexOf(":"));
}

const TitleSchema = z.string().min(1).max(64);

/**
 * HOW a contributed sidebar row draws itself. `disclosure` is the titled, collapsible block
 * the stack has always been; `plain` is a row that draws its own content end to end — no
 * disclosure header, no collapse.
 *
 * The second member exists for NEUTRALITY, not for looks. A sidebar whose only contributable
 * shape is a collapsible block forces everything that is not one — the create buttons, the
 * brand line, the status line, the identity row — to stay hand-written floor JSX, and then
 * the shell is honest about ownership for the blocks and silent about the rest. With `plain`
 * those rows are ordinary contributions: one registry, one per-principal order, two
 * presentations. `presentation` says how a row draws and never whether it is one, which is
 * why arrange mode, the owner-naming DOM and the D4′ placeholder all stay indifferent to it.
 *
 * Absent ≡ {@link DEFAULT_SECTION_PRESENTATION} — every manifest written before this field
 * existed composes, orders and renders exactly as it did.
 */
export const SECTION_PRESENTATIONS = ["disclosure", "plain"] as const;
export const SectionPresentationSchema = z.enum(SECTION_PRESENTATIONS);
export type SectionPresentation = (typeof SECTION_PRESENTATIONS)[number];
export const DEFAULT_SECTION_PRESENTATION: SectionPresentation = "disclosure";

/** Closed value kinds and storage scopes published with the manifest vocabulary. */
export const SETTING_KINDS = ["boolean", "enum"] as const;
export const SETTING_SCOPES = ["principal", "workspace"] as const;
export const SettingScopeSchema = z.enum(SETTING_SCOPES);
export const SettingValueSchema = z.union([z.boolean(), z.string().min(1).max(128)]);
export type SettingValue = z.infer<typeof SettingValueSchema>;
export const SettingKindSchema = z.enum(SETTING_KINDS);
export type SettingKind = (typeof SETTING_KINDS)[number];

/**
 * ONE SETTING A PLUGIN DECLARES: a named preference a principal may hold an opinion about,
 * published at `GET /api/plugins` with everything else the manifest contributes.
 *
 * DECLARATION IS THE WHOLE VOCABULARY, which is what makes the manager's pane generic: the
 * engine renders a control per declared row and knows nothing about what any of them mean, so
 * a stranger's plugin gets the same pane `core.canvas` does without registering a component,
 * and a plugin that declares none is a NAMED absence rather than an empty box. A setting whose
 * effect nobody declared is impossible to store, because a write names a declaration
 * (`engine.plugins.setSetting`) and an undeclared name is refused.
 *
 * `default` is what the setting reads as when this principal has expressed no opinion —
 * carried in the MANIFEST rather than in the store, so a value nobody has ever written is a
 * fact about the plugin's shipped behaviour and the empty override map is the whole of
 * "nothing customized" (the same delta discipline {@link BindingOverridesSchema} keeps).
 *
 * The id is LOCAL; the published name is the pair, `${manifest.id}.${id}`
 * ({@link SettingRefSchema}), so a plugin can never declare a preference outside its own
 * namespace and a stored value always says whose declaration it answers.
 */
const settingFields = {
  id: LocalNameSchema,
  title: TitleSchema,
  scope: SettingScopeSchema.optional(),
};
export const SettingDefSchema = z.discriminatedUnion("kind", [
  z.strictObject({ ...settingFields, kind: z.literal("boolean"), default: z.boolean() }),
  z
    .strictObject({
      ...settingFields,
      kind: z.literal("enum"),
      values: z
        .array(z.strictObject({ id: z.string().min(1).max(128), title: TitleSchema }))
        .min(1)
        .max(32),
      default: z.string().min(1).max(128),
    })
    .superRefine((setting, ctx) => {
      if (new Set(setting.values.map((value) => value.id)).size !== setting.values.length)
        ctx.addIssue({ code: "custom", message: "invalid_setting_enum: duplicate values" });
      if (!setting.values.some((value) => value.id === setting.default))
        ctx.addIssue({
          code: "custom",
          message: "invalid_setting_enum: default is outside values",
        });
    }),
]);
export type SettingDef = z.infer<typeof SettingDefSchema>;

/**
 * One contributed sidebar row. `title` is REQUIRED of both presentations: arrange mode labels
 * the row a reader has grabbed, and a disabled plugin's slot is named by the ENGINE's own
 * placeholder (D4′) — neither may ask a row's component for a name, least of all a plain row
 * whose code may not be loaded.
 *
 * `cluster` is the row saying "I BELONG BESIDE WHOEVER ELSE DECLARES THIS WORD". Rows sharing a
 * cluster render as ONE horizontal row, placed where the cluster's earliest member sits in the
 * live order — so two discreet rows can sit side by side at the rail's foot without the rail
 * knowing which rows those are. The mechanism is deliberately total and dumb: the word is the
 * whole vocabulary, membership is declared rather than positional, and no floor file, panel or
 * engine registry holds a list of a cluster's members (`clusteredSections`,
 * `packages/plugin/src/layout.ts`). That is what makes it survive a plugin being disabled,
 * added or rearranged, and what keeps `core.keys` and `core.plugins` from being named anywhere
 * but in their own manifests (issue #91).
 *
 * NOT `group`: that word is taken, by the placement algebra's capability sets (§Lexicon), and
 * one concept per word is the law. `cluster` is this concept's canon term — a declared set of
 * sidebar rows painted side by side — and the `Cluster` box from `@manifold/ui` is what
 * happens to paint one, the same way the `layout` family's components are named for the shape
 * they draw without touching the canon term.
 *
 * A cluster is not a second order and not a container: the members keep their own ids, their
 * own arrangement seats and their own owners, and a cluster with one live member paints exactly
 * as that member always did.
 *
 * Absent ≡ this row is its own cluster, which is what every manifest written before the field
 * existed says, and why a rail of unclustered rows is byte-identical to today's.
 *
 * `setting` is the row saying "I AM A PREFERENCE, AND THIS DECLARATION HOLDS IT": one of this
 * manifest's OWN `contributes.settings` ids, resolved at composition, and a row whose setting
 * reads false is DROPPED from the sidebar entirely (`visibleSections`,
 * `packages/plugin/src/settings.ts`). Dropped, not marked: a preference is not a disable, so
 * there is no tombstone, no placeholder and no seat kept warm — the difference from D4′ is the
 * whole point, because a disabled plugin's row is an absence the workspace must explain and a
 * row somebody turned off is one they already know about.
 *
 * The reference is LOCAL and stays local: a section may only name a setting its own manifest
 * declares, and assembly refuses one that names anything else with both names in the sentence.
 * A cross-plugin reference would let one plugin's preference erase another's row, which is the
 * shadowing D5 refuses one level up.
 *
 * Absent ≡ this row is unconditional, which is what every manifest written before the field
 * existed says.
 */
export const SectionDefSchema = z.strictObject({
  id: LocalNameSchema,
  title: TitleSchema,
  order: z.number().int(),
  presentation: SectionPresentationSchema.optional(),
  cluster: LocalNameSchema.optional(),
  setting: LocalNameSchema.optional(),
  /** A reference reveals its owning section without hard-coded plugin names in the shell. */
  refKinds: z
    .array(z.enum(ManifoldRefSchema.options.map((option) => option.shape.kind.value)))
    .optional(),
});
export type SectionDef = z.infer<typeof SectionDefSchema>;

/**
 * ONE CONTRIBUTED PANEL: a tile-ref leaf, named `${manifest.id}.${id}` in a `panel` ref.
 *
 * `arranges` is the panel saying "I CONTAIN AN INNER ARRANGEMENT, AND THIS IS ITS NAME". A
 * workspace's arrange mode (`vantage.arranging`) makes panels grabbable inside the tree; a
 * panel that stacks parts of its own — the shell's sidebar and its rows are the first, and
 * deliberately not the only conceivable one — can offer a SECOND scope to arrange inside,
 * and the chrome that offers it needs a word for the thing it is about to zoom into.
 *
 * The declaration is the whole vocabulary, which is the point: the floor renders a zoom-in
 * control for panels that declare one and knows nothing else about what is in there — no
 * enumerated list of arrangeable panels, no "sidebar" anywhere in the engine. What the inner
 * arrangement CONTAINS, how it reorders and where it commits stay entirely the panel's; this
 * says only that it exists and what to call it.
 *
 * Absent ≡ the panel arranges nothing inside itself, which is what every manifest written
 * before this field existed says.
 */
export const PanelDefSchema = z.strictObject({
  id: LocalNameSchema,
  title: TitleSchema,
  arranges: z.strictObject({ title: TitleSchema }).optional(),
});
export type PanelDef = z.infer<typeof PanelDefSchema>;

/**
 * The weight a seat's leaf carries in its split when the manifest declares none. Ratios are
 * RELATIVE: the tile renderer normalizes a split's `ratios` before it paints (flex-grow on a
 * zero basis), so a declaration is a weight against its siblings rather than a promised
 * fraction of a screen nobody has measured yet.
 */
export const DEFAULT_SEAT_RATIO = 1;

/**
 * ONE SEAT A PLUGIN ASKS FOR in a workspace nobody has arranged yet (ADR 0017 §3, stage
 * S17-B): `panel` is one of this manifest's OWN contributed panels, `order` places the seat
 * among every other plugin's, and `ratio` weights its leaf against its siblings.
 *
 * An INTENT, not an arrangement. The engine composes the default workspace tree out of the
 * ENABLED roster's seats, so a first-boot tree is a function of which plugins are on rather
 * than of a constant some floor file kept beside a favourite pair of panel names. A principal
 * who has arranged a workspace is untouched by it: their tree is stored, and this composes
 * only the default for the ones who never did.
 *
 * DUMB AND TOTAL on purpose. A seat names a panel and a place in one row; it cannot express
 * nesting, and it deliberately carries no address — seats holding a `manifold://` referent are
 * stage S17-C and a version-bumped protocol change (ADR 0017 R3), which this field must not
 * pre-empt by shipping half of that wire early.
 *
 * Absent ≡ the plugin seats nothing, which is exactly what every manifest written before this
 * field existed says, and why one manifest line is the whole cost of keeping today's tree.
 */
export const SeatDefSchema = z.strictObject({
  panel: LocalNameSchema,
  order: z.number().int(),
  ratio: z.number().positive().optional(),
});
export type SeatDef = z.infer<typeof SeatDefSchema>;

/**
 * WHICH TOOLBAR a contributed tool paints into. The engine owns the closed vocabulary of
 * toolbars that exist; plugins own the tools inside one, so two plugins contributing `select`
 * into different toolbars is not a collision (D5 still refuses two plugins claiming the same
 * `id` WITHIN one toolbar).
 *
 * `canvas` is the freeform discipline's tool strip (`core.canvas`'s own two modes, `core.canvas.draw`'s
 * pen — {@link CanvasToolbar}). `arrange` is `core.arrange`'s floating F8 editor toolbar
 * (issue #89) — a workspace-level toolbar, unrelated to any one container.
 *
 * Absent ≡ `canvas`, which is what every `tools` row written before this field existed means:
 * the only toolbar the product had. A tool naming a toolbar the reading strip does not draw
 * from is simply invisible there — the same "declare and let the ref filter" shape `arranges`,
 * `cluster` and every other closed-vocabulary field in this file already uses.
 */
export const TOOLBARS = ["canvas", "arrange"] as const;
export const ToolbarSchema = z.enum(TOOLBARS);
export type Toolbar = (typeof TOOLBARS)[number];
export const DEFAULT_TOOLBAR: Toolbar = "canvas";

/**
 * A BROWSER PATH SEGMENT, as a claim: lowercase, digits and dashes, which is exactly the
 * shape the browser's own router matches (`PLUGIN_ROUTE`, `packages/web/src/app.tsx`, which
 * tests captured segments against this pattern rather than spelling the class a second time).
 * A segment carries no slash, so `/uri/<rest>` splits unambiguously into the claim and the
 * rest nobody but the claimant reads.
 */
export const ROUTE_SEGMENT_PATTERN = /^[a-z][a-z0-9-]*$/;
export const RouteSegmentSchema = z.string().regex(ROUTE_SEGMENT_PATTERN).max(32);

/**
 * ONE CONTRIBUTED ROUTE: the URL space a plugin owns, named by its first path segment
 * (`core.uri` claims `uri` and serves `/uri/<rest>`).
 *
 * The manifest counterpart the web channel went without: a route was a registration-time
 * convention, so the roster could not publish the paths a build answers on and two plugins
 * claiming one segment shadowed each other by registration order. Declaring it makes the
 * claim DATA — published at `GET /api/plugins`, refused with both offenders named when two
 * manifests want one segment (D5), and the reason a registration for an undeclared segment
 * contributes nothing, exactly as a smuggled panel does.
 *
 * A segment is claimed GLOBALLY, unlike a panel: there is one URL space, and `/uri/` means
 * one plugin's route or nobody's.
 *
 * Absent ≡ this plugin claims no path, which is what every manifest written before the field
 * existed says.
 */
export const RouteDefSchema = z.strictObject({
  segment: RouteSegmentSchema,
  title: TitleSchema,
});
export type RouteDef = z.infer<typeof RouteDefSchema>;

/**
 * What a plugin declares it adds to the assembly. Each list is bounded, because a
 * manifest is read on every roster fan-out and a plugin contributing hundreds of anything
 * is a plugin that should be several.
 *
 * `panels` are tile-ref leaves (the workspace shell is itself a composition of them, and one
 * may declare an inner arrangement of its own — see {@link PanelDefSchema}), `seats`
 * are where those panels ask to sit in a workspace nobody has arranged (see
 * {@link SeatDefSchema}), `sections` are sidebar rows ordered by their declared `order` (see
 * {@link SectionDefSchema}), `elements` are canvas element renderers keyed by wire type,
 * `tools` are toolbar tools, and `routes` are the URL spaces it claims (see
 * {@link RouteDefSchema}).
 */
const ContributesSchema = z.strictObject({
  harness: z.lazy(() => HarnessDefinitionSchema).optional(),
  panels: z.array(PanelDefSchema).max(8).default([]),
  streams: z.lazy(() => z.array(StreamDescriptorSchema).max(MAX_STREAM_DESCRIPTORS)).optional(),
  /**
   * WHERE THIS PLUGIN'S PANELS ASK TO SIT in the default workspace ({@link SeatDefSchema}).
   * Optional rather than defaulted to `[]`: absence is a MEANING here — the plugin seats
   * nothing — so every manifest written before the field existed keeps exactly the sense it
   * had, and a build's default tree stays the sum of what its roster asked for.
   */
  seats: z.array(SeatDefSchema).max(8).optional(),
  sections: z.array(SectionDefSchema).max(8).default([]),
  /**
   * A contributed element kind: `type` is the wire type stored in scene documents, and
   * `placement` is how the algebra must treat it (G1). Traits are DATA here for the same
   * reason they are data in `ITEM_KINDS` — legality follows from the declaration.
   * Absent placement ≡ `DEFAULT_ELEMENT_PLACEMENT_TRAITS`: free-floating canvas furniture.
   * `presentation` declares body-only or titled framing per container discipline. Absence
   * stays absent in the registry; a mount site defaults an undeclared discipline to titlebar.
   */
  elements: z
    .array(
      z.strictObject({
        type: z.string().min(1).max(32),
        title: TitleSchema,
        placement: PlacementTraitsSchema.optional(),
        presentation: z
          .record(ContainerDisciplineSchema, z.enum(["body", "titlebar"]))
          .refine((values) => Object.keys(values).length <= 32, {
            message: "at most 32 element presentation disciplines",
          })
          .optional(),
      }),
    )
    .max(8)
    .default([]),
  /**
   * THE CONTAINER DISCIPLINES THIS PLUGIN RENDERS (#110, building the ruling ratified on
   * #86). A discipline is the value of `Container.discipline` and the key
   * `ProjectionRegistry.renderer` is looked up by, and declaring it carries the placement
   * rows `packages/protocol/src/placement.ts` used to hold as literals for the two
   * disciplines that happened to ship in the box (see {@link DisciplineDefSchema}).
   *
   * Optional rather than defaulted to `[]` for the reason `seats` and `routes` are:
   * absence is a MEANING — this plugin renders no container of its own — so every manifest
   * written before the field existed keeps exactly the sense it had, and the composed
   * roster stays the sum of what its members declared.
   *
   * `max(4)` bounds the payload the way every other list here is bounded, and nothing
   * bounds it lower: the last-segment pun that used to imply one discipline per plugin was
   * never true (`core.compositions` renders `composition`) and is retired on the record in
   * `layout.ts`. What a declaration must actually survive is the global claim — a
   * discipline id is one plugin's or nobody's, and two declarants refuse by name.
   */
  disciplines: z.array(DisciplineDefSchema).max(4).optional(),
  tools: z
    .array(
      z.strictObject({
        id: LocalNameSchema,
        title: TitleSchema,
        toolbar: ToolbarSchema.optional(),
      }),
    )
    .max(8)
    .default([]),
  /**
   * THE EVENT PLANE's vocabulary half (ADR 0012): the kinds this plugin ORIGINATES. Declaring
   * one claims the word globally — assembly refuses a second claimant (D5) — and it is the
   * only way an emission is legal: the engine emits at its doors, and an emission whose kind
   * this list does not hold is refused by name rather than fanned out. So the mechanism is
   * closed while the vocabulary stays open, which is the whole shape of the plane.
   *
   * `id` is an {@link EventKindSchema}, not a `LocalNameSchema`: a kind is snake_case because
   * the durable history and `terminal_event.kind` already spell it that way, and one concept
   * gets one spelling on every plane.
   */
  events: z
    .array(z.strictObject({ id: EventKindSchema, title: TitleSchema }))
    .max(16)
    .default([]),
  /**
   * THE URL SPACES THIS PLUGIN CLAIMS ({@link RouteDefSchema}). Optional rather than
   * defaulted to `[]` for the reason `seats` is: absence is a MEANING — this plugin answers
   * on no path of its own — so every manifest written before the field existed keeps exactly
   * the sense it had, and the browser's route table stays the sum of what the roster claimed.
   */
  routes: z.array(RouteDefSchema).max(8).optional(),
  /**
   * THE PREFERENCES THIS PLUGIN DECLARES ({@link SettingDefSchema}). Optional rather than
   * defaulted to `[]` for the reason `seats` and `routes` are, and here the absence carries
   * one more consequence worth naming: a plugin that declares nothing serializes byte-for-byte
   * as it did before this field existed, so adding the vocabulary moved no wire and the
   * manager's pane for such a plugin is a named absence rather than an empty form.
   */
  settings: z.array(SettingDefSchema).max(8).optional(),
});

/**
 * What one plugin needs of another. Requirement and ORDER are separate axes: `required`
 * refuses assembly when the named plugin is missing or disabled, `incompatible` refuses
 * when it is present, `optional` refuses nothing and exists so a client can explain a
 * degraded experience. Ordering is declared separately (`after`), because "I need X" and "put
 * me after X" are different sentences and conflating them forces authors to invent a
 * dependency to get a sequence.
 *
 * `reason` is user-facing text: a dependency failure is read by a human deciding what to
 * turn on, and demanding the sentence at authoring time costs nothing.
 */
export const PLUGIN_DEPENDENCY_TYPES = ["required", "optional", "incompatible"] as const;
export type PluginDependencyType = (typeof PLUGIN_DEPENDENCY_TYPES)[number];
export const PluginDependencySchema = z.strictObject({
  type: z.enum(PLUGIN_DEPENDENCY_TYPES),
  reason: z.string().min(1).max(200).optional(),
});
export type PluginDependency = z.infer<typeof PluginDependencySchema>;

/**
 * The declarations, keyed BY the plugin depended on. A map rather than a list because a
 * manifest naming the same plugin twice is not a thing to refuse at compose time — it is a
 * thing the shape should make unsayable — and because a reader asking "what does this say
 * about `core.shell`?" indexes instead of scanning.
 */
export const PluginDependencyMapSchema = z.record(PluginIdSchema, PluginDependencySchema);
export type PluginDependencyMap = z.infer<typeof PluginDependencyMapSchema>;

/**
 * The version of the DATA a plugin owns, which is not the version of its code: a plugin
 * ships many releases against one storage shape. The split is what makes the compatibility
 * rule statable — minor differences proceed untouched, a major difference is a migration
 * the plugin must name, and stored-newer-than-code is refused rather than guessed at.
 */
export const PluginDataVersionSchema = z.strictObject({
  major: z.number().int().min(0),
  minor: z.number().int().min(0),
});
export type PluginDataVersion = z.infer<typeof PluginDataVersionSchema>;

/**
 * How this plugin's already-placed nodes render while it is disabled. DECLARATIVE on
 * purpose: the placeholder is drawn by the ENGINE from this data, never by a component the
 * disabled plugin supplies — the plugin whose code may not be loaded cannot be the one
 * asked to draw its own absence.
 *
 * `ghost` keeps the node visible, named and inert (the default, and the only honest answer
 * for something holding a user's work); `hide` skips it at paint while leaving it in the
 * document, for chrome whose absence is not a loss. `label` overrides the name the ghost
 * shows. Absent ≡ `{ mode: DEFAULT_DORMANT_MODE }`.
 */
export const PLUGIN_DORMANT_MODES = ["ghost", "hide"] as const;
export type PluginDormantMode = (typeof PLUGIN_DORMANT_MODES)[number];
export const DEFAULT_DORMANT_MODE: PluginDormantMode = "ghost";
export const PluginDormantSchema = z.strictObject({
  mode: z.enum(PLUGIN_DORMANT_MODES),
  label: z.string().min(1).max(64).optional(),
});
export type PluginDormant = z.infer<typeof PluginDormantSchema>;

/**
 * Every way something of a DISABLED plugin may still be live — the complete list, closed.
 * A disable gates a plugin's active contributions; these three are the declared carve-outs that
 * survive it, one per plane:
 *
 *   `cleanup`  an action stays dispatchable while disabled (D12), so nobody is ever locked
 *              out of removing a thing the plugin created.
 *   `dormant`  the render plane: placed nodes stay in the document and paint as declared.
 *   `retain`   the data plane, and the ONLY data fate a disable has. Disable is reversible
 *              in every system worth copying; destruction is a separate, separately-gated
 *              verb (see `PLUGIN_PURGE_TARGETS`) that refuses while the plugin is enabled.
 *
 * The list is closed so the carve-out cannot grow quietly: a fourth residual mechanism is
 * a protocol change reviewed as one.
 */
export const PLUGIN_RESIDUAL_MECHANISMS = ["cleanup", "dormant", "retain"] as const;
export type PluginResidualMechanism = (typeof PLUGIN_RESIDUAL_MECHANISMS)[number];

/**
 * What a PURGE destroys. Purge is the deliberate opposite of disable: it is refused while
 * the plugin is enabled, it names what it will remove before it removes it, and a manifest
 * declares its targets so the loss is auditable from data rather than discovered from a
 * hook's behavior.
 *
 *   `storage`   the plugin's namespaced rows.
 *   `elements`  scene elements of the kinds it owns.
 *   `ownership` its element-type reservation — after this, another plugin may claim those
 *               kinds. Until it, the reservation stands precisely so a disabled plugin's
 *               stored data can never be inherited by a stranger.
 */
export const PLUGIN_PURGE_TARGETS = ["storage", "elements", "ownership"] as const;
export const PluginPurgeTargetSchema = z.enum(PLUGIN_PURGE_TARGETS);
export type PluginPurgeTarget = (typeof PLUGIN_PURGE_TARGETS)[number];

/**
 * What a purge REMOVED, per target — every target accounted for, zeros included, because
 * "nothing was there" and "that target was skipped" must not read the same to the caller
 * who just authorised a deletion.
 *
 * `databaseBytes` is the plugin's own SQLite file, journal included, as it stood when the
 * purge deleted it (ADR 0034 §5). It sits BESIDE `removed` rather than inside it because
 * `removed` counts things — rows, elements, reservations — and this is bytes; a plugin that
 * never declared a database reports 0, which is the honest size of a file that never existed.
 */
export const PluginPurgeResultSchema = z.strictObject({
  id: PluginIdSchema,
  removed: z.record(PluginPurgeTargetSchema, z.number().int().min(0)),
  databaseBytes: z.number().int().min(0),
});
export type PluginPurgeResult = z.infer<typeof PluginPurgeResultSchema>;

/**
 * WHERE AN INSTALLED PLUGIN'S CODE IS, inside its bundle. `server: true` means the bundle's
 * `server.js` member is the server half (the file name is fixed — `PLUGIN_BUNDLE_SERVER_FILE`
 * — because the kit inlines its runtime and the hardened loader is one `Bun.spawn`); `web`
 * names the member the browser fetches at `GET /api/plugins/:id/web.js` and imports in-realm,
 * or runs in a dedicated Worker when the installer hardened the row. Either half alone is a
 * plugin; a bundle naming neither is refused `no_entry`.
 *
 * `styles: true` means the bundle's `styles.css` member (`PLUGIN_BUNDLE_STYLES_FILE`, fixed
 * like the server's) is the web half's skin, served at `GET /api/plugins/:id/styles.css` and
 * injected by the loader while the row is enabled — admitted only when every selector is
 * rooted at the plugin's own class (ADR 0025 §7, `stylesheet_unscoped`). Additive and optional:
 * absent is exactly the pre-#258 bundle, one JavaScript member per half and no ink, so no
 * wire version moved.
 */
export const PluginEntrySchema = z.strictObject({
  web: z.string().min(1).max(128).optional(),
  server: z.boolean().optional(),
  styles: z.boolean().optional(),
  /** A portable React build also carries the fixed web.worker.js entry. Not a mode choice. */
  worker: z.literal(true).optional(),
});
export type PluginEntry = z.infer<typeof PluginEntrySchema>;

/**
 * THE CEILING on a plugin's own SQLite file (ADR 0034 §4): the engine's number, not the
 * plugin's word. A manifest may ask for less and gets what it asked for; a manifest asking
 * for more is refused here, at the wire, rather than clamped silently somewhere downstream.
 * It lives in the protocol because it bounds a MANIFEST field, and a manifest is wire.
 */
export const CEILING_DATABASE_MAX_BYTES = 4 * 1024 * 1024 * 1024;

/**
 * How many capabilities one manifest may declare. Named rather than inline because the
 * install door's `grant` is bounded by it too (a grant can never exceed a declaration), and a
 * bound restated at the second site is a bound that drifts at the third.
 */
export const MAX_MANIFEST_CAPABILITIES = 16;

/** Publisher order, not a semantic-version guess, chooses the preferred release. */
export const MAX_PLUGIN_UPDATE_FAMILY = 16;
export const MAX_PLUGIN_RELEASES = 32;
export const MAX_PLUGIN_CHANGELOG_BYTES = 64 * 1024;
export const PluginReleaseSourceSchema = z.string().min(1).max(2048);
export const PluginReleaseArtifactSchema = z.strictObject({
  id: PluginIdSchema,
  url: PluginReleaseSourceSchema,
  sha256: z.string().regex(/^[a-fA-F0-9]{64}$/),
});
export type PluginReleaseArtifact = z.infer<typeof PluginReleaseArtifactSchema>;
export const PluginReleaseSchema = z.strictObject({
  version: z.string().min(1).max(32),
  url: PluginReleaseSourceSchema,
  sha256: z.string().regex(/^[a-fA-F0-9]{64}$/),
  family: PluginReleaseArtifactSchema.array()
    .max(MAX_PLUGIN_UPDATE_FAMILY - 1)
    .optional(),
});
export const PluginReleaseFeedSchema = PluginReleaseSchema.array().min(1).max(MAX_PLUGIN_RELEASES);
export type PluginReleaseFeed = z.infer<typeof PluginReleaseFeedSchema>;

export const PluginManifestSchema = z
  .strictObject({
    id: PluginIdSchema,
    version: z.string().min(1).max(32),
    title: TitleSchema,
    description: z.string().max(500),
    /**
     * The ceiling on this plugin's authority: every action it declares must ask for a subset
     * of these, and a dispatch intersects them with the CALLER's caps. Declaring caps here is
     * what makes a manifest auditable without reading the plugin's code.
     *
     * A member is one of the ENGINE's capabilities or one of this plugin's OWN
     * (`<thisId>:<name>`, ADR 0035) — and the namespace is checked below, because a ceiling
     * that could name `otherPlugin:admin` would let a manifest declare authority over a
     * vocabulary it does not own.
     */
    capabilities: AuthoredCapSchema.array().max(MAX_MANIFEST_CAPABILITIES),
    /**
     * An essential plugin cannot be disabled: the workspace has no way to render itself
     * without it, so the refusal is kinder than the blank screen. `core.shell` is the only
     * essential plugin this wave.
     *
     * The refusal it raises is the `essential` member of `PLUGIN_REFUSAL_REASONS` — a CLASS,
     * not a sentence, and one of several: a system with a single reason to refuse a disable
     * grows its second one immediately (a required dependency, an owned element type), and
     * clients that switched on prose would have to be rewritten each time.
     */
    essential: z.boolean().optional(),
    contributes: ContributesSchema,
    machine: MachineHalfSchema.optional(),
    /**
     * Declared relationships. Absent ≡ none, which is every manifest written before this
     * field existed: a plugin naming nothing composes exactly as it did.
     */
    dependencies: PluginDependencyMapSchema.optional(),
    /**
     * SOFT ordering: compose me after these, if they are here at all. A missing id in this
     * list is not an error — that is the whole difference from a dependency — and the
     * resulting order (topological, ties by lexicographic id) is the order lifecycle hooks
     * fan out in, so "after" is a statement about sequence and nothing else.
     */
    after: PluginIdSchema.array().max(16).optional(),
    /** Absent ≡ unversioned data: nothing to migrate, nothing to refuse. */
    dataVersion: PluginDataVersionSchema.optional(),
    /**
     * ONE SQLITE FILE OF THIS PLUGIN'S OWN, beside its key-value ref (ADR 0034). Declaring it
     * is what makes `ctx.database` exist: a plugin that says nothing here has no file, no slice
     * and nothing for a purge to delete, which is every manifest written before this field.
     * `maxBytes` is a REQUEST — the engine grants it up to `CEILING_DATABASE_MAX_BYTES` and
     * defaults it when absent — so a runaway plugin fills its own file and nothing else.
     */
    database: z
      .strictObject({
        maxBytes: z.number().int().positive().max(CEILING_DATABASE_MAX_BYTES).optional(),
      })
      .optional(),
    /** Absent ≡ `{ mode: DEFAULT_DORMANT_MODE }` — a named, inert ghost. */
    dormant: PluginDormantSchema.optional(),
    /**
     * What a purge of this plugin would destroy, declared for audit visibility. It is a
     * DESCRIPTION, never a trigger: nothing here is bound to the disable verb.
     */
    purges: PluginPurgeTargetSchema.array().max(PLUGIN_PURGE_TARGETS.length).optional(),
    /**
     * Which halves an INSTALLED bundle runs, and where (ADR 0016 §8 stage 2). Absent for every
     * in-tree manifest: a package compiled into the build is found by the two `assembly.ts`
     * files, not by this field. `PluginBundleSchema` requires it.
     */
    entry: PluginEntrySchema.optional(),
    /**
     * Where this plugin comes from, for a reader who wants to look: the repository its code
     * lives in (the manager shows it), a homepage, and a changelog — which the update flow
     * (#238) reads to say what a newer version changes. Absent ≡ the author said nothing, which
     * is every in-tree manifest.
     */
    links: z
      .strictObject({
        repository: z.string().url().max(512).optional(),
        homepage: z.string().url().max(512).optional(),
        changelog: z.string().url().max(512).optional(),
      })
      .optional(),
    /** Declared release feed; retrieval uses the installer's ordinary source policy. */
    releases: PluginReleaseSourceSchema.optional(),
  })
  /*
    A PLUGIN DECLARES ITS OWN CAPABILITIES AND NOBODY ELSE'S (ADR 0035).

    The check is here rather than at assembly because this schema is where every reader meets
    a manifest — the bundle door, the kit's `pack`, the unpacked watcher, assembly itself — and
    a namespace rule enforced at one of them is a rule the others admit. `null` from
    `pluginCapNamespace` is one of the engine's own names (or a malformed one the array's
    element schema already refused), which is why the loop only has the foreign case to say.
  */
  .check((ctx) => {
    for (const cap of ctx.value.capabilities) {
      const namespace = pluginCapNamespace(cap);
      if (namespace === null || namespace === ctx.value.id) continue;
      ctx.issues.push({
        code: "custom",
        input: cap,
        path: ["capabilities"],
        message: `capability "${cap}" is in plugin "${namespace}"'s namespace, not "${ctx.value.id}"'s`,
      });
    }
  });
export type PluginManifest = z.infer<typeof PluginManifestSchema>;

/**
 * WHAT AUTHORITY AN ACTION IS GRADED FOR.
 *
 * `workspace` is the default and the wave-1 rule: a token scoped to one container cannot
 * authorize a workspace-grade mutation, the precedent `core.space.place` and every
 * workspace route already set.
 *
 * `container` says the action's whole effect is confined to ONE container, so a
 * container-scoped token may open it — the door then evaluates the action's declared caps
 * AT that container, and the handler is contractually bound to keep every effect inside
 * it. The container comes from the TOKEN (`ctx.containerScope`), never from the arguments:
 * authority that depended on parsed arguments would force the ladder to validate shape
 * before authority, and a caller would learn an action's schema by knocking on a door it
 * may not open.
 *
 * A workspace-grade caller invoking a `container` action gets `containerScope: null` and
 * the handler resolves its target the way it always did.
 */
export const ACTION_SCOPES = ["workspace", "container"] as const;
export const ActionScopeSchema = z.enum(ACTION_SCOPES);
export type ActionScope = (typeof ACTION_SCOPES)[number];

/**
 * A validated structured reference at an own-property path in the action input.
 *
 * The cap may be one of this plugin's own (ADR 0035), and that pairing is the whole point of
 * the two halves together: `{ cap: "atyrode.babel:archive", target: ["machine"] }` is how a
 * plugin asks the waterfall a question about its OWN authority at a node the engine addresses
 * but has no capability for.
 */
export const ActionRequirementSchema = z.strictObject({
  cap: AskableCapSchema,
  target: z.array(z.string().min(1).max(128)).min(1).max(8),
});
export type ActionRequirement = z.infer<typeof ActionRequirementSchema>;
export const ActionRequirementsSchema = z.array(ActionRequirementSchema).min(1).max(64);
export const ActionTracePolicySchema = z.enum(["redacted", "opaque"]);
export const ActionRunAccessSchema = z.enum([
  "policy",
  "teardown",
  "delegate",
  "inspect",
  "runner",
]);
export type ActionRunAccess = z.infer<typeof ActionRunAccessSchema>;

/**
 * Only native job/resource/service APIs can discharge these at concrete targets.
 *
 * That is the whole membership rule, and it is why `machines:read` belongs here beside
 * `machines:run`: the only ways to exercise it are `engine.machines.repository` and
 * `engine.jobs.describe`, both native, both at `manifold://machine/<id>`. The set is not the
 * governed subset — `services:read` and `services:configure` are in it and are not governed —
 * so a non-governed native read is exactly the shape it already carries. Its absence left a
 * door able to be lent the authority to make a machine RUN something but not to read what that
 * machine is, which the vocabulary orders the other way round, and which made `describe`
 * unreachable for any plugin door after that read moved onto the narrower word (#739).
 */
const NATIVE_DELEGATE_CAPS: readonly Cap[] = [
  "machines:read",
  "machines:run",
  "jobs:read",
  "jobs:input",
  "jobs:cancel",
  "locations:read",
  "locations:write",
  "locations:create",
  "operations:invoke",
  "services:read",
  "services:invoke",
  "services:configure",
  "network:host",
];
export const ActionDelegatesSchema = z
  .array(CapSchema)
  .max(NATIVE_DELEGATE_CAPS.length)
  .refine(
    (caps) => caps.every((cap) => NATIVE_DELEGATE_CAPS.includes(cap)),
    "unsupported delegated capability",
  )
  .refine((caps) => new Set(caps).size === caps.length, "duplicate delegated capability");

/** Result publication is a bounded projection, never an invocation grant. */
export const ACTION_RESULT_PROJECTION_MAX_BYTES = 1_048_576;
export const ActionResultProjectionDigestSchema = z.string().regex(/^[a-f0-9]{64}$/);

/** Exact publication approval owned by a trusted operator or launcher, never model input. */
export const ActionResultApprovalSchema = z.strictObject({
  door: z
    .string()
    .min(1)
    .max(256)
    .refine((door) => !door.includes("*")),
  contractDigest: ActionResultProjectionDigestSchema,
  maxResultBytes: z.number().int().positive().max(ACTION_RESULT_PROJECTION_MAX_BYTES).optional(),
});
export type ActionResultApproval = z.infer<typeof ActionResultApprovalSchema>;
export const ActionResultApprovalsSchema = z
  .array(ActionResultApprovalSchema)
  .max(64)
  .refine((entries) => new Set(entries.map((entry) => entry.door)).size === entries.length);
export type ActionResultApprovals = z.infer<typeof ActionResultApprovalsSchema>;

/** Reversible because plugin and local names cannot contain underscores. */
export function agentToolName(door: string): string {
  const split = door.lastIndexOf(".");
  if (
    split < 0 ||
    !PluginIdSchema.safeParse(door.slice(0, split)).success ||
    !LocalNameSchema.safeParse(door.slice(split + 1)).success
  )
    throw new Error("unsupported agent tool name");
  const name = `manifold_${door.replaceAll(".", "_")}`;
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(name)) throw new Error("unsupported agent tool name");
  return name;
}
export const ActionResultProjectionSchema = JsonProjectionSchema.extend({
  /** Exact selected leaves containing domain-redacted text, never additional subtrees. */
  textFields: JsonProjectionSchema.shape.fields.optional(),
  maxResultBytes: z.number().int().positive().max(ACTION_RESULT_PROJECTION_MAX_BYTES),
}).superRefine((policy, ctx) => {
  if (policy.textFields === undefined) return;
  try {
    compileJsonProjection(policy.fields, policy.textFields);
  } catch (error) {
    if (!(error instanceof JsonProjectionError)) throw error;
    ctx.addIssue({
      code: "custom",
      path: ["textFields"],
      message: "text fields must name exact selected leaves",
    });
  }
});
export type ActionResultProjection = z.infer<typeof ActionResultProjectionSchema>;

/** Bind a trusted output selection to the exact schema-normalized fields and limits. */
export async function actionResultProjectionDigest(
  policy: ActionResultProjection,
): Promise<string> {
  const encoded = new TextEncoder().encode(
    JSON.stringify(ActionResultProjectionSchema.parse(policy)),
  );
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** A projection refusal does not change the outcome of an action that already succeeded. */
export const ActionProjectedResultSchema = z.discriminatedUnion("ok", [
  z.strictObject({
    ok: z.literal(true),
    contractDigest: ActionResultProjectionDigestSchema,
    data: z.unknown(),
  }),
  z.strictObject({
    ok: z.literal(false),
    contractDigest: ActionResultProjectionDigestSchema,
    code: z.enum(["projection_invalid", "projection_limit"]),
  }),
]);
export type ActionProjectedResult = z.infer<typeof ActionProjectedResultSchema>;

/**
 * One action, published. `input` and `result` are JSON Schemas rather than zod shapes,
 * because the audience is a stranger's agent reading `GET /api/protocol` — the door's own
 * validators are generated from the same definitions, so the published schema is the
 * schema the dispatcher enforces, never a hand-written description of it.
 */
export const ActionSummarySchema = z
  .strictObject({
    /** Fully qualified: `${pluginId}.${localName}`. */
    name: z.string(),
    title: z.string(),
    /** What the CALLER must hold: the engine's capabilities, or this plugin's own (ADR 0035). */
    caps: AuthoredCapSchema.array(),
    /** Native API ceiling only; never caller permission, a grant, or target admission. */
    delegates: ActionDelegatesSchema.optional(),
    /**
     * A cleanup action stays dispatchable while its plugin is disabled (D12: creation and
     * administration die on disable, removal survives — nobody is locked out of deleting).
     * Published so a client can tell which affordances outlive a toggle.
     */
    cleanup: z.boolean().optional(),
    /**
     * A reserved core identity action's autonomous-run lifecycle exception. `policy`,
     * `teardown` and `inspect` remain reachable while ordinary authority is suspended;
     * `delegate` publishes target-relative authority that the identity mechanism re-evaluates
     * against the requested child envelope. Assembly refuses this metadata outside `core.access`.
     */
    runAccess: ActionRunAccessSchema.optional(),
    /** A declaration required from active autonomous callers, never authority or reasoning. */
    agentJustification: z.literal("required").optional(),
    /**
     * The authority grade this door is written for. Published (defaulted, so an older reader
     * that never saw the field reads the conservative answer) because "may my container-scoped
     * token call this?" is a question a client must be able to answer from the vocabulary alone.
     */
    scope: ActionScopeSchema.default("workspace"),
    requirements: ActionRequirementsSchema.optional(),
    trace: ActionTracePolicySchema.optional(),
    input: z.record(z.string(), z.unknown()),
    result: z.record(z.string(), z.unknown()),
    /** Absent by default; selected primitive leaves may be disclosed by an opted-in launcher. */
    resultProjection: ActionResultProjectionSchema.optional(),
  })
  .superRefine((action, ctx) => {
    if (action.runAccess !== undefined && action.resultProjection !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["resultProjection"],
        message: "lifecycle actions cannot publish result projections",
      });
    }
  });
export type ActionSummary = z.infer<typeof ActionSummarySchema>;

/**
 * Where a roster row's code came from. `builtin` is the ENGINE's own doors (enablement,
 * purge): same manifest, same action schemas, same dispatch ladder, but no toggle — the
 * mechanism that turns plugins on is not itself a plugin. `plugin` is everything else,
 * including every core plugin, which is what makes "core is not privileged" checkable
 * rather than merely claimed. A distributed plugin will be `plugin` too, so the marketplace
 * wave needs no new roster shape.
 */
export const PLUGIN_SOURCES = ["builtin", "plugin"] as const;
export const PluginSourceSchema = z.enum(PLUGIN_SOURCES);
export type PluginSource = (typeof PLUGIN_SOURCES)[number];

/**
 * What the last lifecycle transition DID, when it did not simply work. A hot toggle runs
 * trusted in-process teardown, and teardown of arbitrary code is unreliable — so failure
 * is a representable state rather than an assertion: the disable ALWAYS completes (a
 * shared workspace is never wedged on one plugin's cleanup) and the roster says so, to
 * every connected principal at once. Absent ≡ `ok`.
 *
 * The two `isolate_` states are what a RUNNER can be in (ADR 0016 §6): an installed row's
 * child is being spawned (`isolate_starting`), or it crashed past `ISOLATE_CRASH_BUDGET` and
 * the supervisor stopped respawning it (`isolate_crashed`) — a degraded row every principal
 * sees, rather than a log line somebody greps. Only rows carrying `install` ever hold them.
 */
export const PLUGIN_LIFECYCLE_STATES = [
  "ok",
  "enable_failed",
  "disable_failed",
  "isolate_starting",
  "isolate_crashed",
] as const;
export const PluginLifecycleStateSchema = z.enum(PLUGIN_LIFECYCLE_STATES);
export type PluginLifecycleState = (typeof PLUGIN_LIFECYCLE_STATES)[number];

/**
 * The named classes of refusal a plugin can meet. A refusal is a CLASS, never a sentence:
 * clients switch on it, agents branch on it, and prose stays a courtesy. `essential` was
 * the first one and is why this is a list — a system with one reason to refuse grows a
 * second immediately.
 *
 *   `essential`               the workspace cannot render itself without it.
 *   `builtin`                 an engine door has no toggle to flip.
 *   `unknown_plugin`          well-formed id, no such row.
 *   `missing_dependency`      a `required` dependency is not composed.
 *   `incompatible_dependency` an `incompatible` dependency IS composed.
 *   `dependency_disabled`     a `required` dependency is present but off; re-enable it
 *                             first, rather than run this plugin against a missing peer.
 *   `data_downgrade`          stored data is newer than the code that would read it.
 *   `data_migration_missing`  a major data version differs and no migration names the gap.
 *   `element_type_owned`      another plugin — possibly a disabled one — owns that element
 *                             type. A reservation outlives a disable so stored nodes can
 *                             never be inherited by a stranger.
 *   `still_enabled`           a purge was asked of a plugin that is still on.
 *   `developer_mode_off`      an UNPACKED row (`install.mode: "unpacked"`, ADR 0025 §4) while
 *                             the workspace's developer mode is off: the directory it is built
 *                             from is admitted only behind `engine.plugins.setDeveloperMode`,
 *                             so enable, authoring and the rebuild loop all refuse by this name.
 *   `stylesheet_unscoped`     the bundle's `styles.css` has a selector whose leftmost compound
 *                             is not the plugin's own root class, a rule with no class at
 *                             all, or a form the ownership walk cannot read at all — an
 *                             at-rule outside its dialect, a rule nested in a rule (ADR 0025
 *                             §7, S13 at load, #410): ink has one owner, and a sheet that
 *                             reaches past its root — or that hides where it reaches — is a
 *                             second writer for somebody's family. Named at the install door
 *                             for a bundle and the unpacked build, and here for an enable that
 *                             re-verifies a stored bundle (R8): the detail is the offending
 *                             selector or form, with its line.
 *
 * A row carries at most one, and the roster carries every row, so a client renders "why"
 * without a second call: which dependency is off is read from this row's manifest against
 * the other rows' `enabled`.
 */
export const PLUGIN_REFUSAL_REASONS = [
  "essential",
  "builtin",
  "unknown_plugin",
  "missing_dependency",
  "incompatible_dependency",
  "dependency_disabled",
  "data_downgrade",
  "data_migration_missing",
  "element_type_owned",
  "still_enabled",
  "developer_mode_off",
  "stylesheet_unscoped",
] as const;
export const PluginRefusalReasonSchema = z.enum(PLUGIN_REFUSAL_REASONS);
export type PluginRefusalReason = (typeof PLUGIN_REFUSAL_REASONS)[number];

/**
 * Why an INSTALL or UNINSTALL was refused (ADR 0016 §8 stage 2), the same discipline as the
 * toggle refusals above: a class a client switches on, with the detail after a colon.
 *
 *   `artifact_unreadable`  the source could not be fetched or read at all.
 *   `artifact_invalid`     the bytes are not a bundle this engine reads — wrong format, a
 *                          manifest that fails the schema, or an assembly refusal (a duplicate
 *                          id, a `core.` squat) caught at install time rather than at boot.
 *   `hash_mismatch`        the bytes hash to something other than the `sha256` the installer
 *                          pinned — refused fail-closed, at install and again at every boot (R8).
 *   `already_installed`    that id is installed at a different hash and `replace` was not asked.
 *   `not_installed`        an uninstall or replace of an id nobody installed.
 *   `namespace_reserved`   the manifest claims `engine.` or `core.`, which only the build may.
 *   `still_enabled`        an uninstall or replace of a plugin that is still on: disable first.
 *   `storage_retained`     an uninstall of a plugin whose storage still holds rows (#233): purge
 *                          first, or pass `purge: true` and the door purges before it uninstalls.
 *                          Uninstall never destroys data on its own, and it never strands any.
 *   `no_entry`             the manifest names no half to run — nothing to install.
 *   `stylesheet_unscoped`  `entry.styles` is set and the sheet reaches past the plugin's root
 *                          class, or carries a form the ownership walk cannot read (ADR 0025
 *                          §7, #410): the detail names the offender and its line.
 *                          The same class the toggle answers, because the unpacked build path
 *                          and a bundle install meet the rule at this door.
 */
export const PLUGIN_INSTALL_REFUSALS = [
  "artifact_unreadable",
  "artifact_invalid",
  "hash_mismatch",
  "already_installed",
  "not_installed",
  "namespace_reserved",
  "still_enabled",
  "storage_retained",
  "no_entry",
  "stylesheet_unscoped",
] as const;
export const PluginInstallRefusalSchema = z.enum(PLUGIN_INSTALL_REFUSALS);
export type PluginInstallRefusal = (typeof PLUGIN_INSTALL_REFUSALS)[number];

/**
 * Where an installed row's bytes COME FROM (ADR 0025 §4). `bundle` is an artifact somebody
 * handed to the install door; `unpacked` is a directory under `<data>/authored/<id>/` the hub
 * itself watches and rebuilds, pinning the row by the hash of the bytes it built. Absent ≡
 * `bundle`, so every row written before the field existed keeps exactly the sense it had. The
 * same install path, the same loader, the same pin — the mode says only who packs.
 */
export const PLUGIN_INSTALL_MODES = ["bundle", "unpacked"] as const;
export const PluginInstallModeSchema = z.enum(PLUGIN_INSTALL_MODES);
export type PluginInstallMode = (typeof PLUGIN_INSTALL_MODES)[number];

export const PluginBuildCompatibilitySchema = z.strictObject({
  status: z.enum(["compatible", "unknown", "incompatible"]),
  issues: z
    .strictObject({
      component: z.string().min(1).max(128),
      built: z.string().max(128).nullable(),
      current: z.string().min(1).max(128),
      kind: z.enum(["unknown", "incompatible"]),
    })
    .array()
    .max(16),
});
export type PluginBuildCompatibility = z.infer<typeof PluginBuildCompatibilitySchema>;

/** A bounded server observation, never an authorization to install the discovered code. */
export const PluginUpdateStatusSchema = z.discriminatedUnion("state", [
  z.strictObject({ state: z.enum(["unchecked", "checking"]) }),
  z.strictObject({
    state: z.literal("current"),
    checkedAt: z.number().int().nonnegative(),
  }),
  z.strictObject({
    state: z.literal("available"),
    checkedAt: z.number().int().nonnegative(),
    version: z.string().min(1).max(32),
    family: PluginIdSchema.array().min(1).max(MAX_PLUGIN_UPDATE_FAMILY),
  }),
  z.strictObject({
    state: z.literal("failed"),
    checkedAt: z.number().int().nonnegative(),
    message: z.string().min(1).max(1000),
  }),
]);
export type PluginUpdateStatus = z.infer<typeof PluginUpdateStatusSchema>;

export const PLUGIN_UPDATE_REFUSALS = [
  "review_missing",
  "review_expired",
  "review_stale",
  "consent_required",
  "update_blocked",
  "update_unavailable",
  "update_failed",
] as const;
export const PluginUpdateRefusalSchema = z.enum(PLUGIN_UPDATE_REFUSALS);
export type PluginUpdateRefusal = z.infer<typeof PluginUpdateRefusalSchema>;

export const PluginUpdateDescriptionSchema = z.strictObject({
  version: z.string().min(1).max(32),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  source: PluginReleaseSourceSchema,
  capabilities: AuthoredCapSchema.array().max(MAX_MANIFEST_CAPABILITIES),
  dependencies: PluginDependencyMapSchema,
  entry: PluginEntrySchema,
  machine: z.boolean(),
  dataVersion: PluginDataVersionSchema.nullable(),
  builtAgainst: z.record(z.string(), z.string()).optional(),
});
export const PluginUpdateMemberSchema = z.strictObject({
  id: PluginIdSchema,
  title: z.string().min(1).max(128),
  current: PluginUpdateDescriptionSchema.extend({
    /** Effective state at review, distinct from the retained desired state below. */
    enabled: z.boolean(),
  }).nullable(),
  candidate: PluginUpdateDescriptionSchema,
  /** Retained desired enablement; assembly may still hold a candidate off. */
  enabled: z.boolean(),
  hardened: z.boolean(),
  storedDataVersion: PluginDataVersionSchema.nullable(),
  capabilitiesAdded: AuthoredCapSchema.array().max(MAX_MANIFEST_CAPABILITIES),
  capabilitiesRemoved: AuthoredCapSchema.array().max(MAX_MANIFEST_CAPABILITIES),
  grantedCaps: AuthoredCapSchema.array(),
  migrationRequired: z.boolean(),
  compatibility: PluginBuildCompatibilitySchema,
  changelog: z
    .strictObject({
      text: z.string().max(MAX_PLUGIN_CHANGELOG_BYTES),
      source: z.string().min(1).max(2048),
    })
    .nullable(),
});
export type PluginUpdateMember = z.infer<typeof PluginUpdateMemberSchema>;
export const PluginUpdateReviewSchema = z.strictObject({
  digest: z.string().regex(/^[a-f0-9]{64}$/),
  rootId: PluginIdSchema,
  createdAt: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative(),
  members: PluginUpdateMemberSchema.array().min(1).max(MAX_PLUGIN_UPDATE_FAMILY),
  blockers: z
    .strictObject({ id: PluginIdSchema, reason: z.string().min(1).max(1000) })
    .array()
    .max(MAX_PLUGIN_UPDATE_FAMILY * 4),
});
export type PluginUpdateReview = z.infer<typeof PluginUpdateReviewSchema>;
export const PluginUpdateReviewRequestSchema = z.strictObject({ id: PluginIdSchema });
export const PluginUpdateReviewResultSchema = z.discriminatedUnion("state", [
  z.strictObject({
    state: z.literal("current"),
    rootId: PluginIdSchema,
    checkedAt: z.number().int().nonnegative(),
  }),
  z.strictObject({ state: z.literal("review"), review: PluginUpdateReviewSchema }),
]);
export type PluginUpdateReviewResult = z.infer<typeof PluginUpdateReviewResultSchema>;
export const PluginUpdateApplyRequestSchema = z.strictObject({
  digest: z.string().regex(/^[a-f0-9]{64}$/),
  /** Exact ceiling expansions acknowledged per member; withheld old grants stay withheld. */
  consent: z
    .strictObject({
      id: PluginIdSchema,
      capabilities: AuthoredCapSchema.array().max(MAX_MANIFEST_CAPABILITIES),
    })
    .array()
    .max(MAX_PLUGIN_UPDATE_FAMILY),
});
export type PluginUpdateApplyRequest = z.infer<typeof PluginUpdateApplyRequestSchema>;
export const PluginUpdateApplyResultSchema = z.strictObject({
  rootId: PluginIdSchema,
  installed: z
    .strictObject({
      id: PluginIdSchema,
      version: z.string().min(1).max(32),
      sha256: z.string().regex(/^[a-f0-9]{64}$/),
    })
    .array()
    .min(1)
    .max(MAX_PLUGIN_UPDATE_FAMILY),
});
export type PluginUpdateApplyResult = z.infer<typeof PluginUpdateApplyResultSchema>;

/**
 * What an installer CONSENTED TO, on the row (ADR 0016 §5): the artifact pinned by hash, where
 * it came from as the installer spelled it, and the capability set granted — the roster is
 * where every principal reads a grant, so it lives here and nowhere else. `grantedCaps` is
 * intersected with the manifest's declared caps at rung 4, BEFORE the caller's own caps, so a
 * refusal names the plugin's grant rather than the caller. Attribution mirrors `changedBy` /
 * `changedAt` on the row: an install changes what everyone is looking at.
 *
 * `refusal` is why this install cannot SERVE right now, as distinct from the row's own
 * `refusal` (why it cannot be toggled): a bundle that no longer hashes to `sha256` at boot is
 * `hash_mismatch` here and `enable_failed` on the row, never loaded (R8, fail-closed).
 */
export const PluginInstallSchema = z.strictObject({
  /** Who packed the bytes; absent is `bundle` (ADR 0025 §4). */
  mode: PluginInstallModeSchema.optional(),
  /** Installer-selected hardening; absent is in-realm (ADR 0025). */
  hardened: z.boolean().optional(),
  /** Shared React and floor-package versions used to build the bundle. */
  builtAgainst: z.record(z.string(), z.string()).optional(),
  compatibility: PluginBuildCompatibilitySchema.optional(),
  update: PluginUpdateStatusSchema.optional(),
  sha256: z.string().length(64),
  /** The url or path as given, so an operator can tell where a stranger's code came from. */
  source: z.string().max(2048),
  grantedCaps: AuthoredCapSchema.array(),
  installedBy: z.string().min(1).max(128),
  installedAt: z.number().int().min(0),
  refusal: PluginInstallRefusalSchema.optional(),
});
export type PluginInstall = z.infer<typeof PluginInstallSchema>;

/**
 * A plugin as the roster describes it: everything a client needs to render the plugin, its
 * doors, its state and WHO put it in that state, from one document.
 *
 * Attribution is on the row rather than in an event because enablement is workspace-global
 * and hot — one principal's toggle changes what everyone else is looking at, right now —
 * and a placeholder that can say "disabled by alex" is the difference between a change and
 * a glitch. Both fields are absent (or null, as a store row reads them) until someone
 * actually toggles the plugin.
 */
export const PluginRosterEntrySchema = z.strictObject({
  manifest: PluginManifestSchema,
  enabled: z.boolean(),
  /** Effective execution selected by the host, independent of installation provenance. */
  hardened: z.boolean().optional(),
  source: PluginSourceSchema,
  actions: ActionSummarySchema.array(),
  /** Absent ≡ `ok`: the plugin's last transition did what it said. */
  lifecycle: PluginLifecycleStateSchema.optional(),
  /** Why this row cannot be toggled right now — a lock in the UI, not a hidden failure. */
  refusal: PluginRefusalReasonSchema.optional(),
  /** A structurally incompatible plugin is discoverable but contributes nothing at runtime. */
  held: z
    .strictObject({
      reason: z.string().min(1),
      by: PluginIdSchema.optional(),
      minimum: z.number().int().positive().optional(),
    })
    .refine(({ reason, minimum }) => reason !== "repack_required" || minimum !== undefined, {
      message: "repack_required must name the minimum accepted hardened contract",
    })
    .optional(),
  changedBy: z.string().min(1).max(128).nullish(),
  changedAt: z.number().int().min(0).nullish(),
  /**
   * Present iff the plugin was installed. Its `hardened` member records the installer's
   * choice; the roster's own `hardened` reports effective execution for every provenance.
   * Bundled first-party and builtin rows have no install record.
   */
  install: PluginInstallSchema.optional(),
});
export type PluginRosterEntry = z.infer<typeof PluginRosterEntrySchema>;

/**
 * The whole assembly, as every principal sees it: server-owned, served at
 * `GET /api/plugins`, and pushed on the connection-level `plugins` session frame whenever
 * it changes. Registration is shared state, never a client's private guess.
 */
export const PluginRosterSchema = PluginRosterEntrySchema.array();
export type PluginRoster = z.infer<typeof PluginRosterSchema>;

/**
 * THE DISCIPLINE ROSTER: discipline id → the declaration its plugin contributed, derived
 * from a published `PluginRoster` (#110).
 *
 * It is the container-side twin of `rosterElementTraits`, and it lives HERE — in the
 * protocol package, beside the roster shape it reads — rather than in the plugin engine,
 * because both halves of the system and `GET /api/protocol` itself need the same
 * projection, and a second walk of the same array is a second answer to "what disciplines
 * exist" waiting to disagree (docs/CONTRACTS.md §One authoritative implementation). One derivation, one door.
 *
 * DISABLED plugins are included, deliberately and for exactly the reason element traits
 * are: their containers are still in the index. A disable decides who RENDERS a container
 * (D4′ — the engine-owned placeholder), never what composes with it, and a container that
 * became unplaceable and un-unplaceable at once because somebody toggled a plugin is a
 * workspace nobody can tidy. UNINSTALLED is the different case, and the one that has no
 * row here at all: `unknown_discipline`.
 *
 * Iteration order follows the roster, and a duplicate id cannot reach here — assembly
 * refuses one, naming every claimant.
 */
export function rosterDisciplines(
  roster: PluginRoster,
): ReadonlyMap<string, DisciplineDeclaration> {
  const disciplines = new Map<string, DisciplineDeclaration>();
  for (const entry of roster) {
    if (entry.held !== undefined) continue;
    for (const discipline of entry.manifest.contributes.disciplines ?? []) {
      disciplines.set(discipline.id, discipline);
    }
  }
  return disciplines;
}

/**
 * Why a dispatch was refused. The ladder is MONOTONIC and evaluated in this order, so a
 * caller learns the FIRST thing wrong rather than a summary: the action must exist, its
 * plugin must be enabled, the caller must be allowed to reach the door at all, the caller
 * must hold every declared cap, the arguments must parse, and any required autonomous
 * declaration must be safe before the handler may refuse on state it alone can see (`refused`).
 *
 * `unavailable` is the LAST rung and the isolation runner's alone (ADR 0016 §6): the door
 * exists, its plugin is on, the caller is allowed and the arguments would have been graded —
 * but the child process that holds the handler is not running (crashed past its budget) or
 * did not answer inside `ISOLATE_DISPATCH_DEADLINE_MS`. A hung isolate is a refusal, never a
 * stuck promise, and it is traced like every other rung. An in-realm door never answers it.
 */
export const ACTION_DENIAL_RULES = [
  "unknown_action",
  "plugin_disabled",
  "policy_required",
  "policy_stale",
  "forbidden",
  "invalid_args",
  "justification_required",
  "invalid_justification",
  "refused",
  "unavailable",
] as const;
export const ActionDenialSchema = z.strictObject({
  rule: z.enum(ACTION_DENIAL_RULES),
  message: z.string(),
});
export type ActionDenial = z.infer<typeof ActionDenialSchema>;
export type ActionDenialRule = (typeof ACTION_DENIAL_RULES)[number];

/**
 * What the action door answers. A denial is a 200 carrying `ok: false` — the same shape
 * `core.space.place` uses for a refused placement, because a refusal is an ANSWER about
 * authority or state, not a transport failure.
 */
export const ActionOutcomeSchema = z.union([
  z.strictObject({
    ok: z.literal(true),
    result: z.unknown(),
    projection: ActionProjectedResultSchema.optional(),
  }),
  z.strictObject({ ok: z.literal(false), denial: ActionDenialSchema }),
]);
export type ActionOutcome = z.infer<typeof ActionOutcomeSchema>;

/**
 * WHY ONE PLUGIN'S HANDLER COULD NOT OPEN ANOTHER PLUGIN'S DOOR (ADR 0041).
 *
 * `ctx.actions.call({ plugin, action, input })` is the one verb a server half has for a
 * DECLARED dependency, and it acquires no authority: the callee runs under the principal of
 * the request the caller is serving, so its own rungs grade that principal exactly as they
 * grade a client's. What is left to refuse is the composition, and — like the denial ladder
 * these sit beside — the list is MONOTONIC and published in the order it is walked, so a
 * caller learns the first thing wrong rather than a summary:
 *
 *   `dispatch_cycle`         the callee is already on this trace's plugin stack, the caller
 *                            included: a plugin reaching for its own door, and A -> B -> A one
 *                            frame later. Asked FIRST because it is the one refusal no
 *                            manifest edit can answer — composition refuses a self-dependency
 *                            and a dependency cycle outright (ADR 0013 §5.3, §5.6) — so
 *                            naming the edge here would be advice that cannot be taken.
 *   `dispatch_depth`         the call would be the {@link MAX_ACTION_CALL_DEPTH}+1st plugin
 *                            frame on one trace. The other bound on the trace, so it is asked
 *                            with it.
 *   `undeclared_dependency`  the callee is not in the caller's manifest `dependencies` as
 *                            `required` or `optional`. Composition is declared, never
 *                            discovered: an edge nobody wrote down is not an edge.
 *   `dependency_unavailable` a declared dependency that is not composed, or is disabled
 *                            right now. Reachable only for an `optional` edge — a `required`
 *                            one absent or off is a composition refusal (ADR 0013 §5.1) —
 *                            and the caller stays enabled either way: there is no cascade.
 *   `unknown_action`         the callee publishes no door by that name.
 *   `caller_ceiling`         the callee door declares a capability the CALLING plugin's own
 *                            ceiling does not hold (`granted ∩ declared`, the ceiling its own
 *                            doors are bounded by). A plugin never does through a sibling what
 *                            it could not have declared for itself, so an install grant that
 *                            withheld `plugins:manage` is still withheld when the plugin
 *                            depends on `engine.plugins` and an owner opens one of its doors.
 *                            A SECOND bound, not a narrowing of the principal: the grade at
 *                            the callee is still the caller's request principal, and both have
 *                            to pass. The detail names the caller, the callee door and the cap.
 *   `capability`             the callee's OWN scope/grant/capability rung refused this
 *                            principal — at the callee, never admitted by the caller's
 *                            authority. The callee's sentence is the detail.
 *   `refused`                the callee answered a denial of its own — a handler's
 *                            `{ refused }`, its `invalid_args`, or an isolated callee that
 *                            did not answer — carried through with its rule and sentence.
 *
 * The detail after `": "` names the plugins involved, caller first (`test.a -> test.b.echo`),
 * because a refusal a reader cannot attribute to an edge is a refusal they cannot act on.
 */
export const ACTION_CALL_REFUSALS = [
  "dispatch_cycle",
  "dispatch_depth",
  "undeclared_dependency",
  "dependency_unavailable",
  "unknown_action",
  "caller_ceiling",
  "capability",
  "refused",
] as const;
export const ActionCallRefusalSchema = z.enum(ACTION_CALL_REFUSALS);
export type ActionCallRefusal = (typeof ACTION_CALL_REFUSALS)[number];

/**
 * How many PLUGINS one trace may carry. A client's dispatch is the first frame, so the bound
 * admits seven nested calls under it — deeper than any composition anyone has argued for, and
 * finite, which is the property that matters: a cycle is already refused by name, and this is
 * what stops a long chain that never repeats an id from spending the hub's stack instead.
 */
export const MAX_ACTION_CALL_DEPTH = 8;

/**
 * What `ctx.actions.call` takes. `action` is the callee's LOCAL name — the door's full name is
 * `${plugin}.${action}`, so a caller can never spell a name outside the plugin it declared a
 * dependency on — and `input` is the callee door's own arguments, graded by the callee's
 * published schema and by nothing here.
 */
export const ActionCallArgsSchema = z.strictObject({
  plugin: PluginIdSchema,
  action: LocalNameSchema,
  input: z.unknown(),
});
export type ActionCallArgs = z.infer<typeof ActionCallArgsSchema>;

/**
 * A BINDING's published name: its owning plugin's id followed by one local name
 * (`core.shell.arrange`), which is the same pair rule an action name obeys — a plugin can
 * never name a key outside its own namespace, and a full name always says who owns it.
 * Spelled as its own pattern rather than reused from {@link PluginIdSchema} because the last
 * segment is a LOCAL name and those admit interior capitals (`core.keys.setBinding`), while
 * every segment before it is a plugin-id segment.
 */
export const BINDING_ID_PATTERN = /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*){1,2}\.[a-z][a-zA-Z0-9-]*$/;
export const BindingIdSchema = z.string().regex(BINDING_ID_PATTERN).max(96);
export type BindingId = z.infer<typeof BindingIdSchema>;

/**
 * ONE KEYSTROKE — an optional `Mod+` prefix followed by the value `KeyboardEvent.key`
 * reports: `F9`, `a`, `ArrowUp`, `?`, `Mod+k`. Bounded and otherwise unconstrained on
 * purpose: the browser owns this vocabulary, an editor captures the value from a real
 * keystroke rather than composing it, and a server that tried to enumerate legal keys would
 * be a second, always-stale keyboard map. The GRAMMAR is the engine's
 * (`@manifold/plugin`'s `parseKeystroke`), which is also why the bound has never moved: a
 * prefixed stroke was already a string this schema accepted, so a stored override written by
 * any build parses in every other.
 */
export const BindingKeySchema = z.string().min(1).max(24);

/**
 * ONE PRINCIPAL'S BINDING OVERRIDES: the keys this principal has rebound, as binding id →
 * key. Server-saved rather than device-local, because a principal is one actor across every
 * device they sit at (multiplayer-first, docs/CONTRACTS.md §Producer-neutral behavior) and a rebind that lived in one
 * browser's storage would be a fact no other client could read.
 *
 * An override is a DELTA, never a table: what a workspace answers to is the declared rows
 * with this map applied at composition (`composeBindings`), so a plugin that ships a new key,
 * a plugin that is disabled and a plugin that renames its own row all keep exactly the
 * meaning they have without anybody rewriting stored overrides. A row with no entry here
 * answers its declared default, which is why the empty map is the whole of "nothing rebound".
 *
 * Bounded at 64 entries: this is one principal's deltas over a key table a manifest bounds at
 * a handful of rows per plugin, and an unbounded per-principal map is a write door with no
 * ceiling.
 */
export const MAX_BINDING_OVERRIDES = 64;
export const BindingOverridesSchema = z
  .record(BindingIdSchema, BindingKeySchema)
  .refine((overrides) => Object.keys(overrides).length <= MAX_BINDING_OVERRIDES, {
    message: `at most ${MAX_BINDING_OVERRIDES} binding overrides`,
  });
export type BindingOverrides = z.infer<typeof BindingOverridesSchema>;

/**
 * A SETTING's published name: the declaring plugin's id followed by the local id its manifest
 * gave it (`core.canvas.new-canvas`).
 *
 * Composed from the two schemas that already own those halves rather than spelled as a third
 * regex — the pair rule is one rule, and a copy of it here would be a second answer to "what
 * may a qualified name look like" that drifts the first time either half changes.
 */
export const SettingRefSchema = z.templateLiteral([PluginIdSchema, ".", LocalNameSchema]);
export type SettingRef = z.infer<typeof SettingRefSchema>;

/**
 * ONE PRINCIPAL'S SETTING VALUES: the preferences they have expressed an opinion about, as
 * setting ref → value. Server-saved for the reason a rebinding is (docs/CONTRACTS.md §Producer-neutral behavior): a principal
 * is one actor across every device they sit at, so a preference held in one browser's storage
 * would be a fact none of their other clients could read.
 *
 * A DELTA over the declarations, never a table — the same discipline
 * {@link BindingOverridesSchema} keeps, and the reason a plugin may change its own default,
 * be disabled, or be removed entirely without anybody rewriting stored values. A ref with no
 * entry reads its manifest's `default`, which is why the empty map is the whole of "nothing
 * customized", and a stored ref no declaration answers is DROPPED at composition rather than
 * pruned from the store: a plugin turned off for a week must find its preferences intact.
 *
 * Values are booleans or declared enum strings.
 *
 * Bounded at 128 entries: a manifest may declare eight settings, so this is a ceiling on a
 * principal's deltas over a roster of some size rather than a limit any real workspace meets,
 * and an unbounded per-principal map is a write door with no ceiling.
 */
export const MAX_PLUGIN_SETTING_VALUES = 128;
export const PluginSettingValuesSchema = z
  .record(SettingRefSchema, SettingValueSchema)
  .refine((values) => Object.keys(values).length <= MAX_PLUGIN_SETTING_VALUES, {
    message: `at most ${MAX_PLUGIN_SETTING_VALUES} plugin setting values`,
  });
export type PluginSettingValues = z.infer<typeof PluginSettingValuesSchema>;

/**
 * The plugin vocabulary, published — the counterpart of `placementVocabulary()`. A
 * stranger's agent reading `GET /api/protocol` learns what a manifest may declare, what a
 * roster row can say about state and attribution, and every CLOSED set a refusal can name,
 * from the declarations themselves rather than from prose that drifts away from them.
 *
 * What this does NOT contain is which plugins a given server composed: that is the live
 * assembly, handed in through `ProtocolExtras`, because this package describes shapes
 * and never their inhabitants.
 */
export function pluginVocabulary(): Record<string, unknown> {
  return {
    engineNamespace: ENGINE_NAMESPACE_PREFIX,
    streams: streamVocabulary(),
    /*
      Published beside it because the reservation is a rule an AUTHOR has to know before
      choosing an id: `core.` is taken, and a manifest under it fails assembly unless the
      distribution registered it. What is NOT published is who inhabits the namespace — that is
      the live roster's answer, and this package describes shapes, never their inhabitants.
    */
    coreNamespace: CORE_NAMESPACE_PREFIX,
    /*
      THE OPEN HALF OF THE CAPABILITY VOCABULARY (ADR 0035). The engine's closed enum is
      published as `caps` in `instanceVocabulary`'s neighbour and named in every action row;
      what a reader cannot infer from an enum is that a NAMESPACED name is legal at all, and
      in whose namespace. So the form is published as a schema — pattern and bound — beside
      the namespace rules that decide who may declare one: a manifest may name only its own,
      and a grant may name anybody's, because a grant is written by a principal rather than
      by a plugin.
    */
    pluginCap: z.toJSONSchema(PluginCapSchema),
    maxPluginCapLength: MAX_PLUGIN_CAP_LENGTH,
    authoredCap: z.toJSONSchema(AuthoredCapSchema),
    sources: PLUGIN_SOURCES,
    dependencyTypes: PLUGIN_DEPENDENCY_TYPES,
    dormantModes: PLUGIN_DORMANT_MODES,
    defaultDormantMode: DEFAULT_DORMANT_MODE,
    residualMechanisms: PLUGIN_RESIDUAL_MECHANISMS,
    purgeTargets: PLUGIN_PURGE_TARGETS,
    lifecycleStates: PLUGIN_LIFECYCLE_STATES,
    refusalReasons: PLUGIN_REFUSAL_REASONS,
    /*
      The install door's own refusal classes and the block an installed row carries (ADR 0016
      §5, §8 stage 2): a stranger's agent installing a plugin learns what it may be told and
      what the roster will then say about the grant, from the same read as everything else.
    */
    installRefusals: PLUGIN_INSTALL_REFUSALS,
    installModes: PLUGIN_INSTALL_MODES,
    install: z.toJSONSchema(PluginInstallSchema),
    releaseFeed: z.toJSONSchema(PluginReleaseFeedSchema),
    updateReview: z.toJSONSchema(PluginUpdateReviewSchema),
    updateRefusals: PLUGIN_UPDATE_REFUSALS,
    denialRules: ACTION_DENIAL_RULES,
    actionScopes: ACTION_SCOPES,
    /*
      THE ONE VERB A SERVER HALF HAS ONTO A SIBLING (ADR 0041), published as data for the same
      reason the ladder is: an author composing `atyrode.babel -> atyrode.code` learns the
      member's name, the arguments it takes, every class it can be refused with and the depth
      bound from one read, rather than from this tree. It rides the plugin vocabulary because
      a call on a declared dependency is a fact about MANIFESTS — the edge is what admits it.
    */
    actionCall: {
      member: "actions.call",
      args: z.toJSONSchema(ActionCallArgsSchema),
      refusals: ACTION_CALL_REFUSALS,
      maxDepth: MAX_ACTION_CALL_DEPTH,
    },
    defaultElementPlacement: DEFAULT_ELEMENT_PLACEMENT_TRAITS,
    sectionPresentations: SECTION_PRESENTATIONS,
    defaultSectionPresentation: DEFAULT_SECTION_PRESENTATION,
    toolbars: TOOLBARS,
    defaultToolbar: DEFAULT_TOOLBAR,
    defaultSeatRatio: DEFAULT_SEAT_RATIO,
    seat: z.toJSONSchema(SeatDefSchema),
    manifest: z.toJSONSchema(PluginManifestSchema),
    action: z.toJSONSchema(ActionSummarySchema),
    outcome: z.toJSONSchema(ActionOutcomeSchema),
    rosterEntry: z.toJSONSchema(PluginRosterEntrySchema),
    purgeResult: z.toJSONSchema(PluginPurgeResultSchema),
    /*
      The rebind vocabulary, published beside the manifest's: an agent reading this learns
      that keys are declared by plugins and overridden per principal, and what shape a
      `GET /api/bindings` answer and a `core.keys.setBinding` argument take.
    */
    maxBindingOverrides: MAX_BINDING_OVERRIDES,
    bindingOverrides: z.toJSONSchema(BindingOverridesSchema),
    /*
      The settings vocabulary, on the same terms: which value kinds a declaration may take,
      and what shape a `GET /api/settings` answer and an `engine.plugins.setSetting` argument
      take. The DECLARATIONS themselves ride in the manifest schema above — a setting is a
      contribution, so an agent reading `manifest` already knows how one is spelled.
    */
    settingKinds: SETTING_KINDS,
    settingScopes: SETTING_SCOPES,
    maxPluginSettingValues: MAX_PLUGIN_SETTING_VALUES,
    pluginSettingValues: z.toJSONSchema(PluginSettingValuesSchema),
  };
}
